<script lang="ts" setup>
import type { FieldFilter, FilterPathSegment, FilterRelationalQuantifier, PrimitiveFieldPath } from '#layers/data-model/public';
import type { SegmentedFieldFilter } from './utils/filter-path';
import type {
	FilterSchemaCollection,
	FilterSchemaPrimitiveField,
} from './utils/filter-schema';
import type { UIFilterOperator } from './utils/filter-ui-operator';
import { createReusableTemplate, until } from '@vueuse/core';
import { useEmitAsProps } from 'reka-ui';
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { delay } from '#layers/base/public';
import { FilterOperator, getFieldFilterOperatorValue, isCurrentUserVariable, isListValueOperator, isSingleValueOperator, useFieldTypeTemplate } from '#layers/data-model/public';
import { allowsSegmentedFilter, useFilterGate } from '#layers/filters/composables/use-filter-gate';
import { useFilterOperator } from '#layers/filters/composables/use-filter-operator';
import { createGroup } from '#layers/filters/composables/use-logical-filter';
import { getNarrowedOperatorsForField, narrowOperators, resolveDefaultOperator } from './utils/filter-operators';
import { isPrimitiveSegment, parseSegmentedFilter, resolveFieldPathSegments, serializeSegmentedFilter } from './utils/filter-path';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { modelToUi, seedOperatorState, UI_NOT_NULL, uiToModel } from './utils/filter-ui-operator';
import { useFilterConfigResolver } from './utils/use-filter-config-resolver';

const {
	includeRelationFields = true,
	collection,
	enableVariables,
	readonly,
	autoAdvance,
} = defineProps<{
	collection: FilterSchemaCollection;
	includeRelationFields?: boolean;
	hideGroupActions?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	autoAdvance?: boolean;
	enableVariables?: boolean;
}>();

const emits = defineEmits<{
	remove: [];
	duplicate: [];
	turnIntoGroup: [];
	autoAdvanceDone: [];
}>();

const filter = defineModel<FieldFilter>({ required: true });
const { operator, getOperatorLabel } = useFilterOperator(filter);
const schema = injectFilterSchemaContext();
const { findPrimitiveFieldById } = schema;
const gate = useFilterGate();
const actionEmits = useEmitAsProps(emits);

const internalFilter = computed({
	get(): SegmentedFieldFilter | null {
		try {
			return parseSegmentedFilter(filter.value, collection, schema);
		}
		catch {
			return null;
		}
	},
	set(newFilter: SegmentedFieldFilter | null) {
		if (!newFilter) return;
		filter.value = serializeSegmentedFilter(newFilter, schema);
	},
});

const segments = computed(() => internalFilter.value?.segments ?? []);

function onFieldPathSelect(index: number, fieldPath: PrimitiveFieldPath) {
	if (!internalFilter.value) return;

	const resolved = resolveFieldPathSegments(internalFilter.value.segments, index, fieldPath, collection, schema);
	if (!resolved) return;

	const seedOperator = resolved.terminalPrimitiveField ? getDefaultOperatorOption(resolved.terminalPrimitiveField, resolved.segments) : undefined;

	internalFilter.value = { segments: resolved.segments, ...seedOperatorState(seedOperator) };
}

function withQuantifier(segments: FilterPathSegment[], index: number, quantifier: FilterRelationalQuantifier) {
	const segment = segments[index];
	if (segment?.type !== 'to-many') return;
	return segments.map((current, position) => position === index ? { ...segment, quantifier } : current);
}

function onUpdateQuantifier(index: number, quantifier: FilterRelationalQuantifier) {
	const newSegments = internalFilter.value && withQuantifier(internalFilter.value.segments, index, quantifier);
	if (newSegments) internalFilter.value = { ...internalFilter.value, segments: newSegments };
}

function allowsQuantifier(index: number, quantifier: FilterRelationalQuantifier) {
	const newSegments = internalFilter.value && withQuantifier(internalFilter.value.segments, index, quantifier);
	return !!newSegments && allowsSegmentedFilter(gate, schema, { ...internalFilter.value, segments: newSegments });
}

const primitiveField = computed(() => {
	const finalSegment = segments.value.at(-1);

	if (finalSegment && isPrimitiveSegment(finalSegment)) {
		return findPrimitiveFieldById(finalSegment.fieldId);
	}

	return null;
});

const { resolveFilterConfig } = useFilterConfigResolver();
const { typeOptions } = useFieldTypeTemplate(computed(() => primitiveField.value ?? null));

const filterConfig = computed(() => resolveFilterConfig(primitiveField.value ?? undefined));

const showVariableButton = computed(() => {
	const op = operator.value;
	return enableVariables
		&& !readonly
		&& primitiveField.value != null
		&& (isSingleValueOperator(op) || isListValueOperator(op));
});

function onVariableSelect(variable: string, isList: boolean) {
	if (!internalFilter.value) return;
	const op = operator.value;

	if (isList) {
		const nextOperator = isListValueOperator(op) ? op : FilterOperator.IN;
		internalFilter.value = { ...internalFilter.value, operator: nextOperator, value: variable };
		return;
	}

	if (isListValueOperator(op)) {
		const existing = internalFilter.value.value;
		const list = Array.isArray(existing) ? existing : [];
		internalFilter.value = { ...internalFilter.value, value: [...list, variable] };
		return;
	}

	internalFilter.value = { ...internalFilter.value, value: variable };
}

const operatorOpen = ref(false);
const operatorMenuRef = useTemplateRef<{
	viewportRef?: HTMLDivElement | Readonly<{ value?: HTMLDivElement | null }>;
}>('operatorMenuRef');
const inputGroupRef = useTemplateRef<{ focusFirstInput: () => void }>('inputGroupRef');
const pendingInputFocus = ref(false);
const hasCustomInputGroup = computed(() => Boolean(filterConfig.value?.inputGroup));
let advanceToken = 0;
let operatorSelected = false;

defineExpose({ operatorOpen });

interface AdvanceStep {
	isApplicable: boolean;
	activate: () => void;
	waitUntilPast: () => Promise<boolean>;
}

const advanceSteps = computed<AdvanceStep[]>(() => [
	{
		isApplicable: !hasCustomInputGroup.value,
		activate: () => {
			operatorSelected = false;
			operatorOpen.value = true;
		},
		waitUntilPast: async () => {
			await until(operatorOpen).toBe(false);
			await waitForOperatorMenuToUnmount();
			return operatorSelected;
		},
	},
	{
		isApplicable: true,
		activate: () => {
			focusFirstInput();
		},
		waitUntilPast: async () => true,
	},
]);

function focusFirstInput() {
	if (!inputGroupRef.value) {
		pendingInputFocus.value = true;
		return;
	}

	pendingInputFocus.value = false;
	inputGroupRef.value.focusFirstInput();
}

async function waitForOperatorMenuToUnmount() {
	const exposedViewport = operatorMenuRef.value?.viewportRef;
	const viewport = exposedViewport instanceof HTMLDivElement ? exposedViewport : exposedViewport?.value;
	const content = viewport?.closest<HTMLElement>('[role="listbox"]');
	if (!content?.isConnected) return;

	await new Promise<void>((resolve) => {
		const observer = new MutationObserver(() => {
			if (content.isConnected) return;
			observer.disconnect();
			resolve();
		});

		observer.observe(content.ownerDocument.body, { childList: true, subtree: true });

		if (!content.isConnected) {
			observer.disconnect();
			resolve();
		}
	});
}

watch(inputGroupRef, (inputGroup) => {
	if (!inputGroup || !pendingInputFocus.value) return;

	focusFirstInput();
});

watch(() => autoAdvance, (active) => {
	if (!active) return;
	const token = ++advanceToken;
	void runAdvance(token);
}, { immediate: true });

async function runAdvance(token: number) {
	for (let i = 0; i < advanceSteps.value.length; i++) {
		const step = () => advanceSteps.value[i]!;
		if (!step().isApplicable) continue;
		// Custom groups skip the operator step, so their focus target only exists after the initial render.
		if (i > 0) await nextTick();
		step().activate();
		const shouldContinue = await step().waitUntilPast();
		if (token !== advanceToken) return;
		if (!shouldContinue) break;
		await delay(0);
		if (token !== advanceToken) return;
	}

	if (token === advanceToken) {
		emits('autoAdvanceDone');
	}
}

onBeforeUnmount(() => {
	advanceToken++;
});

const isInvalidFilter = computed(() => {
	if (internalFilter.value === null) {
		return true;
	}

	const finalSegment = segments.value.at(-1);

	if (!finalSegment) {
		return true;
	}

	if (finalSegment && isPrimitiveSegment(finalSegment) && primitiveField.value == null) {
		return true;
	}

	return false;
});

const operatorOptions = computed<{ label: string; value: UIFilterOperator }[]>(() => getOperatorOptions(primitiveField.value ?? undefined, segments.value));

const writableOperator = computed<UIFilterOperator | undefined>({
	get: () => modelToUi(operator.value, getFieldFilterOperatorValue(filter.value)),
	set(op) {
		if (op === undefined || !internalFilter.value) return;

		const prevValue = leavesToManyVariable(op) ? undefined : internalFilter.value.value;

		const next = uiToModel(op, { operator: writableOperator.value, value: prevValue });
		internalFilter.value = { ...internalFilter.value, ...next };
	},
});

function onOperatorSelect(op: UIFilterOperator | undefined) {
	if (op === undefined) return;

	operatorSelected = true;
	writableOperator.value = op;
}

function leavesToManyVariable(nextOperator: UIFilterOperator): boolean {
	const nextIsListOperator = nextOperator !== UI_NOT_NULL && isListValueOperator(nextOperator);

	return isListValueOperator(operator.value)
		&& !nextIsListOperator
		&& isCurrentUserVariable(internalFilter.value?.value);
}

// Offer only operators whose clause, as this row serializes it, the gate accepts. The row's own
// operator stays offered outside the usual menu, so a saved filter keeps its label.
function resolveFieldOperators(field: FilterSchemaPrimitiveField | undefined, segments: FilterPathSegment[]) {
	const config = resolveFilterConfig(field);
	const operators = getNarrowedOperatorsForField(field, (operators) => narrowOperators(operators, config?.supportedOperators)
		.filter((operator) => allowsSegmentedFilter(gate, schema, { segments, operator })), { retain: operator.value });

	return { config, operators };
}

/**
 * Operator a newly added filter seeds with: the field-type's explicit `defaultOperator` when set
 * (decoupled from dropdown order), else the first option. Falls back to the first option value for
 * nullable-only fields whose narrowed operator list is empty (only `_null` / `isn't null` remain).
 */
function getDefaultOperatorOption(field: FilterSchemaPrimitiveField | undefined, segments: FilterPathSegment[]): UIFilterOperator | undefined {
	const { config, operators } = resolveFieldOperators(field, segments);

	if (operators.length === 0) return getOperatorOptions(field, segments)?.[0]?.value;

	return resolveDefaultOperator(operators, config?.defaultOperator);
}

function getOperatorOptions(field: FilterSchemaPrimitiveField | undefined, segments: FilterPathSegment[]) {
	const { config, operators } = resolveFieldOperators(field, segments);
	const operatorLabels = config?.operatorLabels;

	return [
		...operators.map((op) => ({
			label: getOperatorLabel(op, operatorLabels),
			value: op,
		})),
		// "is null" / "isn't null" for nullable fields — both map to the `_null` operator
		// at the model layer (true / false respectively), see filter-ui-operator.ts.
		...(field?.isNullable && allowsSegmentedFilter(gate, schema, { segments, operator: FilterOperator.NULL, value: true })
			? [
					{
						label: getOperatorLabel(FilterOperator.NULL, operatorLabels),
						value: FilterOperator.NULL,
					},
					{
						label: getOperatorLabel(UI_NOT_NULL, operatorLabels),
						value: UI_NOT_NULL,
					},
				] as const
			: []
		),
	];
}

const [DefineFilterError, ReuseFilterError] = createReusableTemplate();

/**
 * A caught render error must still leave a row behind.
 *
 * The boundary swallows the throw and renders the `#error` slot in place of the condition, so a
 * slot that states nothing turns a broken row into an absent one — on a permissions editor that
 * reads as a rule that isn't there. The filter value itself is untouched and still saves, so the
 * row states that it cannot be shown and offers the one edit that always applies: removing it.
 */
function onRenderError(error: unknown) {
	console.error('[FilterSimple] Failed to render a filter condition', error);
}
</script>

<template>
	<DefineFilterError>
		<FilterHandleGroup class="border-error">
			<div class="flex items-center gap-1.5 px-2 text-error">
				<DIcon name="lucide:circle-alert" class="size-4" />
				<span class="text-xs whitespace-nowrap">{{ $t('filters.invalid_filter') }}</span>
			</div>
			<FilterRemove v-bind="actionEmits" :disabled />
		</FilterHandleGroup>
	</DefineFilterError>

	<NuxtErrorBoundary @error="onRenderError">
		<template #error>
			<ReuseFilterError />
		</template>

		<ReuseFilterError v-if="isInvalidFilter" />

		<FilterHandleGroup v-else>
			<FilterFieldPath
				:segments
				:disabled
				:include-relation-fields
				:allows-quantifier
				@update-from-segment="onFieldPathSelect"
				@update-quantifier="onUpdateQuantifier"
			/>

			<DSelectMenu
				ref="operatorMenuRef"
				v-model:open="operatorOpen"
				:model-value="writableOperator"
				:items="operatorOptions"
				value-key="value"
				variant="ghost"
				trailing-icon=""
				class="rounded-none"
				focus-handoff
				:search-input="operatorOptions.length > 5"
				:ui="{
					base: 'px-2',
					content: 'min-w-fit',
					value: 'text-xs',
				}"
				:disabled
				@update:model-value="onOperatorSelect"
			/>
			<FilterInputGroup
				ref="inputGroupRef"
				v-model="internalFilter"
				:primitive-field="primitiveField!"
				:filter-config
				:type-options
				:disabled
				:readonly
				:enable-variables
				@remove="emits('remove')"
			/>
			<FilterVariableButton v-if="showVariableButton" :target-type="primitiveField!.type" :disabled @select="onVariableSelect" />
			<FilterActions v-if="hideGroupActions && !readonly" v-bind="actionEmits" :can-turn-into-group="gate.allowsClause(createGroup([filter]))" :disabled />
			<FilterRemove v-else-if="!readonly" v-bind="actionEmits" :disabled />
		</FilterHandleGroup>
	</NuxtErrorBoundary>
</template>
